
import Vue from "vue"
import "leaflet/dist/leaflet.css"
import L from "leaflet"

// Fix Leaflet marker icons issue
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

interface AddressPosition {
  latitude: number
  longitude: number
}

export default Vue.extend({
  name: 'AddressPositionMap',
  props: {
    position: {
      type: Object as () => AddressPosition | null,
      default: null
    },
    height: {
      type: String,
      default: '300px'
    },
    zoom: {
      type: Number,
      default: 15
    }
  },
  data: () => ({
    map: null as L.Map | null,
    marker: null as L.Marker | null,
    mapInitialized: false,
    mapId: `addressMap_${Math.random().toString(36).substr(2, 9)}`,
    pendingTimers: [] as ReturnType<typeof setTimeout>[],
  }),
  mounted() {
    // Initialize map when component is mounted if position is available
    if (this.position && this.isValidPosition(this.position) && !this.mapInitialized) {
      // Use setTimeout to ensure DOM is fully ready (especially in dialogs)
      this.queueTimer(() => {
        this.$nextTick(() => {
          this.initializeMap()
        })
      }, 100)
    }
  },
  watch: {
    position: {
      handler(newPosition: AddressPosition | null) {
        if (newPosition && this.isValidPosition(newPosition)) {
          if (!this.mapInitialized) {
            this.$nextTick(() => {
              this.initializeMap()
            })
          } else if (this.map) {
            this.updateMapMarker(newPosition)
          }
        }
      },
      immediate: false
    }
  },
  beforeDestroy() {
    this.destroyMap()
  },
  methods: {
    queueTimer(callback: () => void, delay: number) {
      const timerId = setTimeout(() => {
        this.removeTimer(timerId)
        callback()
      }, delay)
      this.pendingTimers.push(timerId)
    },
    removeTimer(timerId: ReturnType<typeof setTimeout>) {
      const index = this.pendingTimers.indexOf(timerId)
      if (index !== -1) {
        this.pendingTimers.splice(index, 1)
      }
    },
    clearPendingTimers() {
      while (this.pendingTimers.length > 0) {
        clearTimeout(this.pendingTimers.pop()!)
      }
    },
    isValidPosition(position: AddressPosition | null): boolean {
      return !!(
        position &&
        typeof position.latitude === 'number' &&
        typeof position.longitude === 'number' &&
        !Number.isNaN(position.latitude) &&
        !Number.isNaN(position.longitude)
      )
    },
    cleanupExistingLeafletMap(mapContainer: HTMLElement) {
      // Container is already initialized, destroy it first
      try {
        const existingMap = (mapContainer as any)._leaflet_id
        if (existingMap) {
          const leafletMap = (L as any).Map.prototype.getContainer ? null : (globalThis as any).L?.maps?.[existingMap]
          if (leafletMap) {
            leafletMap.remove()
          }
        }
      } catch (e) {
        console.warn('Failed to clean up existing Leaflet map:', e)
      }
      // Clear the leaflet ID
      delete (mapContainer as any)._leaflet_id
    },
    initializeMap() {
      if (!this.position || !this.isValidPosition(this.position)) return

      // Destroy existing map if it exists
      if (this.mapInitialized && this.map) {
        this.destroyMap()
      }

      const mapContainer = document.getElementById(this.mapId)
      if (!mapContainer) {
        // Retry if container is not ready yet
        this.queueTimer(() => {
          this.initializeMap()
        }, 50)
        return
      }

      // Check if container has dimensions
      if (mapContainer.offsetWidth === 0 || mapContainer.offsetHeight === 0) {
        // Retry if container doesn't have dimensions yet
        this.queueTimer(() => {
          this.initializeMap()
        }, 50)
        return
      }

      // Check if container is already initialized by Leaflet
      if ((mapContainer as any)._leaflet_id) {
        this.cleanupExistingLeafletMap(mapContainer)
      }

      // Initialize the map
      this.map = L.map(this.mapId, { attributionControl: false }).setView([
        this.position.latitude,
        this.position.longitude
      ], this.zoom)

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(this.map)

      // Add marker for the address
      this.marker = L.marker([
        this.position.latitude,
        this.position.longitude
      ]).addTo(this.map)

      // Force map to invalidate size after a short delay to ensure it renders correctly
      this.queueTimer(() => {
        if (this.map) {
          this.map.invalidateSize()
        }
      }, 100)

      this.mapInitialized = true
    },
    updateMapMarker(position: AddressPosition) {
      if (!this.map || !this.isValidPosition(position)) return

      // Remove existing marker
      if (this.marker) {
        this.map.removeLayer(this.marker)
      }

      // Add new marker
      this.marker = L.marker([position.latitude, position.longitude]).addTo(this.map)
      
      // Update map view
      this.map.setView([position.latitude, position.longitude], this.zoom)
    },
    destroyMap() {
      this.clearPendingTimers()
      if (this.map) {
        this.map.remove()
        this.map = null
        this.marker = null
      }
      this.mapInitialized = false
    }
  }
})
